import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { UserRoleType } from "@coeff/api";
import React, { useEffect, useState } from "react";

import {
  ModalCard,
  ModalHeader,
  Overlay,
  PrimaryBtn,
  RoleOption,
} from "../../pagesV2/Members.styles";

const ROLE_META = [
  {
    value: UserRoleType.Admin,
    label: "Admin",
    desc: "Access to admin tools, billing management, plus all regular member features",
  },
  {
    value: UserRoleType.Member,
    label: "Member",
    desc: "Regular access to Coefficient's features",
  },
] as const;

type Props = {
  /** null = closed. */
  user: {
    name: string;
    isAdmin: boolean;
    /** The only admin left in the domain — demoting them must be blocked. */
    isLastAdmin: boolean;
  } | null;
  busy: boolean;
  onSave: (role: UserRoleType) => void;
  onClose: () => void;
};

/** Workspace Admin/Member picker (radio cards + last-admin guard). */
export const ManageRoleModal: React.FC<Props> = ({ user, busy, onSave, onClose }) => {
  const [roleChoice, setRoleChoice] = useState<UserRoleType>(UserRoleType.Member);

  // Seed the selection from the user's current role each time the modal opens.
  useEffect(() => {
    if (user) {
      setRoleChoice(user.isAdmin ? UserRoleType.Admin : UserRoleType.Member);
    }
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <div className="title">Manage role</div>
            <div className="sub">{user.name}</div>
          </div>
          <button className="close" onClick={onClose}>
            <CloseOutlined />
          </button>
        </ModalHeader>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
          {ROLE_META.map(opt => {
            const selected = roleChoice === opt.value;
            const lastAdmin = user.isLastAdmin && opt.value === UserRoleType.Member;
            return (
              <RoleOption
                key={opt.value}
                selected={selected}
                disabled={lastAdmin}
                onClick={() => !lastAdmin && setRoleChoice(opt.value)}
              >
                <div className="radio">{selected && <div className="dot" />}</div>
                <div>
                  <div className="label">{opt.label}</div>
                  <div className="desc">{opt.desc}</div>
                </div>
              </RoleOption>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
          <PrimaryBtn disabled={busy} onClick={() => onSave(roleChoice)}>
            {busy && <LoadingOutlined spin />}
            {busy ? "Saving…" : "Save"}
          </PrimaryBtn>
        </div>
      </ModalCard>
    </Overlay>
  );
};
